/**
 * Lohnbuchungsbeleg — client-side journal (Buchungssatz) derivation.
 *
 * Mirrors the server `api/.../shared/journal.ts` exactly so the on-screen "Buchungsbeleg"
 * tab, the server-generated Excel/PDF export and the BC clipboard copy all agree. Turns the
 * allocation matrix into aggregated booking entries: one Soll or Haben posting per
 * (account × cost center), netted.
 *
 * Two rules (see the server file for the full rationale):
 *  1. The cell's sign is *presentation* (template `Sign`, or a split line's cost center sitting
 *     on the Haben side) — never the posting direction. We post `|final|` and take the direction
 *     from the template's Soll/Haben accounts.
 *  2. Cost centers belong on Erfolgskonten only; balance-sheet accounts (1…, 2…, 9…) book without.
 *  3. Every posting is in whole Rappen: each line's 4-dp cells are rounded before netting (largest
 *     remainder, line total kept), so the 2-dp BC paste balances exactly.
 */

import type { ComputedVoucher, Journal, JournalEntry } from '../types/lohnBuchungsbeleg';

const BALANCE_TOLERANCE = 0.01;
const ZERO_TOLERANCE = 0.005;

/** Swiss chart of accounts: 1 = Aktiven, 2 = Passiven, 9 = Verrechnung/Abschluss. */
const BALANCE_SHEET_PREFIXES = ['1', '2', '9'];

/** The payroll clearing account (Lohnverrechnung). Its net must be zero. */
export const CLEARING_ACCOUNT = '9';

function round(n: number): number {
    return Math.round(n * 1e6) / 1e6;
}

function acc(v: string | null | undefined): string {
    return (v ?? '').toString().trim();
}

/** Balance-sheet accounts (1…, 2…, 9…) are booked without a cost center. */
export function isBalanceSheetAccount(account: string | null | undefined): boolean {
    const a = acc(account);
    return a.length > 0 && BALANCE_SHEET_PREFIXES.includes(a[0]);
}

/**
 * Round non-negative amounts (up to 4 dp) to Rappen so their sum equals the rounded exact sum
 * (largest-remainder method). Ties go to the larger amount, then to the earlier position.
 */
export function roundToRappen(amounts: number[]): number[] {
    const units = amounts.map(a => Math.round(Math.abs(a) * 10000));
    const cents = units.map(u => Math.floor(u / 100));
    const targetCents = Math.round(units.reduce((s, u) => s + u, 0) / 100);
    let missing = targetCents - cents.reduce((s, c) => s + c, 0);
    const order = units
        .map((u, i) => ({ i, rest: u % 100, u }))
        .filter(x => x.rest > 0)
        .sort((a, b) => b.rest - a.rest || b.u - a.u || a.i - b.i);
    for (const { i } of order) {
        if (missing <= 0) break;
        cents[i] += 1;
        missing -= 1;
    }
    return cents.map(c => c / 100);
}

export function buildJournal(voucher: ComputedVoucher): Journal {
    const nets = new Map<string, number>();
    const texts = new Map<string, { text: string; weight: number }>();

    const add = (account: string, costCenter: string, delta: number, text: string) => {
        if (!account) return;
        if (Math.abs(delta) < 1e-9) return;
        const key = `${account}|${costCenter}`;
        nets.set(key, round((nets.get(key) ?? 0) + delta));
        const weight = Math.abs(delta);
        const prev = texts.get(key);
        if (text && (!prev || weight > prev.weight)) texts.set(key, { text, weight });
    };

    for (const line of voucher.lines) {
        const soll = acc(line.soll);
        const haben = acc(line.haben);
        const sollCc = isBalanceSheetAccount(soll) ? '' : null;   // null = use the cell's cost center
        const habenCc = isBalanceSheetAccount(haben) ? '' : null;

        const amounts = roundToRappen(line.cells.map(c => c.final));
        line.cells.forEach((cell, i) => {
            const amount = amounts[i];
            if (amount < ZERO_TOLERANCE) return;
            const cc = acc(cell.costCenterCode);
            add(soll, sollCc ?? cc, +amount, line.text);
            add(haben, habenCc ?? cc, -amount, line.text);
        });
    }

    const entries: JournalEntry[] = [];
    let totalSoll = 0;
    let totalHaben = 0;
    let clearingNet = 0;
    for (const [key, netRaw] of nets) {
        const net = round(netRaw);
        const [account, costCenter] = key.split('|');
        if (account === CLEARING_ACCOUNT) clearingNet = round(clearingNet + net);
        if (Math.abs(net) < ZERO_TOLERANCE) continue;
        const soll = net > 0 ? net : 0;
        const haben = net < 0 ? -net : 0;
        entries.push({ account, costCenter, soll, haben, text: texts.get(key)?.text ?? '' });
        totalSoll = round(totalSoll + soll);
        totalHaben = round(totalHaben + haben);
    }

    const numish = (s: string) => { const n = Number(s); return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY; };
    entries.sort((a, b) => {
        const an = numish(a.account), bn = numish(b.account);
        if (an !== bn) return an - bn;
        if (a.account !== b.account) return a.account.localeCompare(b.account);
        const ac = numish(a.costCenter), bc = numish(b.costCenter);
        if (ac !== bc) return ac - bc;
        return a.costCenter.localeCompare(b.costCenter);
    });

    const difference = round(totalSoll - totalHaben);
    return {
        period: voucher.period,
        entries,
        totalSoll,
        totalHaben,
        balanced: Math.abs(difference) < BALANCE_TOLERANCE,
        difference,
        clearingAccount: CLEARING_ACCOUNT,
        clearingNet: round(clearingNet),
    };
}
